import React from "react";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import {
  Compass, TrendingUp, Landmark, Settings2, Rocket, Heart, Target,
  Scale, ShieldAlert, Cpu, Wallet, UsersRound, Banknote, Building2,
} from "lucide-react";

// Advisor key -> icon
export const ADVISOR_ICON = {
  chief_of_staff: Compass,
  lead_investor: TrendingUp,
  cfo: Landmark,
  coo: Settings2,
  growth: Rocket,
  customer: Heart,
  strategy: Target,
  legal_admin: Scale,
  risk: ShieldAlert,
  product_tech: Cpu,
  wealth: Wallet,
  people: UsersRound,
  banker: Banknote,
  industry: Building2,
};

const STATUS_STYLE = {
  healthy: "bg-emerald-500/12 text-emerald-500 border-emerald-500/25",
  needs_attention: "bg-amber-500/12 text-amber-500 border-amber-500/25",
  at_risk: "bg-red-500/12 text-red-500 border-red-500/25",
};
const STATUS_BORDER = {
  healthy: "border-l-emerald-500/60",
  needs_attention: "border-l-amber-500/60",
  at_risk: "border-l-red-500/70",
};

function List({ label, items, dotClass }) {
  if (!items?.length) return null;
  return (
    <div className="mt-3">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary mb-1.5">{label}</p>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-text-primary flex gap-2 leading-snug">
            <span className={cn("mt-1.5 w-1.5 h-1.5 rounded-full shrink-0", dotClass)} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AdvisorCard({ advisor }) {
  const { t } = useApp();
  const Icon = ADVISOR_ICON[advisor.key] || Compass;
  const status = advisor.status || "healthy";
  return (
    <div data-testid={`advisor-card-${advisor.key}`}
      className={cn("bg-surface rounded-xl border border-l-4 p-4 transition-all hover:shadow-md", STATUS_BORDER[status])}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-surface-hover flex items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5 text-text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-heading font-bold text-sm truncate">{advisor.name}</h3>
            <span data-testid={`advisor-status-${advisor.key}`}
              className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap", STATUS_STYLE[status])}>
              {t(`adv_status_${status}`)}
            </span>
          </div>
          {advisor.headline && <p className="text-sm text-text-secondary mt-1 leading-snug">{advisor.headline}</p>}
        </div>
      </div>
      <List label={t("adv_findings")} items={advisor.findings} dotClass="bg-text-secondary/50" />
      <List label={t("adv_risks")} items={advisor.risks} dotClass="bg-red-500/60" />
      <List label={t("adv_recommendations")} items={advisor.recommendations} dotClass="bg-primary/60" />
      {advisor.sources?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {advisor.sources.map((s, i) => (
            <span key={i} data-testid={`advisor-source-${advisor.key}-${i}`}
              className="text-[10px] px-2 py-0.5 rounded bg-surface-hover text-text-secondary border max-w-full truncate">
              {s.label || s.type}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
